import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { Container, Card, Button, Alert, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { Lock, Shield } from "react-feather";
import { useAuth } from "../../hooks/auth";

const LoginContainer = styled(Container)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 2rem 1rem;
`;

const LoginCard = styled(Card)`
  max-width: 450px;
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.div`
  padding: 2.5rem 2rem 1.5rem;
  text-align: center;
  background: white;
  border-bottom: 1px solid #dee2e6;
`;

const LogoIcon = styled.div`
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: #495057;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #212529;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #6c757d;
  margin: 0;
`;

const CardBody = styled(Card.Body)`
  padding: 2.5rem 2rem;
`;

const GoogleButton = styled(Button as any)`
  width: 100%;
  padding: 0.875rem;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 6px;
  background: white;
  border: 1px solid #ced4da;
  color: #212529;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #adb5bd;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
  }
`;

const GoogleIcon = styled.svg`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;
  color: #6c757d;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  gap: 1rem;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  font-weight: 500;
  color: #6c757d;
  margin: 0;
`;

export const Login: React.FC = () => {
  const { loginWithGoogle, isAuthenticated, isLoading, error } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner animation="border" />
        <LoadingText>Loading...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <LoginContainer fluid>
      <LoginCard>
        <CardHeader>
          <LogoIcon>
            <Lock size={28} />
          </LogoIcon>
          <Title>Welcome Back</Title>
          <Subtitle>Sign in to access your account</Subtitle>
        </CardHeader>

        <CardBody>
          {error && (
            <Alert variant="danger" className="mb-4">
              <strong>Error:</strong> {error}
            </Alert>
          )}

          <GoogleButton
            variant="light"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <GoogleIcon viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </GoogleIcon>
            <span>Continue with Google</span>
          </GoogleButton>

          <Footer>
            <Shield size={14} />
            <span>
              By signing in, you agree to our Terms of Service and Privacy
              Policy
            </span>
          </Footer>
        </CardBody>
      </LoginCard>
    </LoginContainer>
  );
};
